import type {BiomarkerDisplayRule, RuleSex} from './displayRuleTypes';

export interface ResolveTarget {
    loinc?: string | null;
    assayCode?: string | null;
    testName?: string | null;
    sex?: 'male' | 'female';
}

/** Strip the ELIS ordering prefix ("01; WBC" -> "WBC") and normalize. */
function normalizeName(testName: string): string {
    const withoutPrefix = testName.includes('; ') ? testName.split('; ')[1] : testName;
    return withoutPrefix.toLowerCase().trim();
}

/**
 * True unless both sides carry this key and disagree. A weaker-tier match is
 * eligible when the rule simply lacks the stronger key, or the target didn't
 * supply it — it is disqualified only when both sides have it and it differs.
 */
function noContradiction(targetValue: string | null, ruleValue: string | null): boolean {
    return targetValue === null || ruleValue === null || targetValue === ruleValue;
}

/**
 * Resolve the display rule for a result, most specific key first:
 *
 *   loinc+sex -> loinc+any -> code+sex -> code+any -> alias+sex -> alias+any
 *
 * A weaker tier (assay_code, alias) may still match a rule that also carries
 * a stronger key (loinc) the target didn't supply — a rule is disqualified
 * from a tier only when a key present on BOTH sides actively disagrees, per
 * noContradiction(). "Weaker keys only" would make a dual-keyed rule (e.g.
 * loinc + assay_code) unreachable via its assay_code whenever the incoming
 * result happens to carry no loinc, even though nothing contradicts it. But
 * matching purely on a weaker key while ignoring a real disagreement on a
 * stronger one (target loinc 'A' vs rule loinc 'C') would apply one
 * analyte's bands to a different one just because they share a code or
 * name — worse than falling through to the default, so that case must still
 * lose.
 *
 * Returns null when nothing matches. Null means "unset", and the caller must
 * fall through to the existing lab-range behavior — the override layer is
 * additive and never degrades an assay it does not cover.
 *
 * When sex is unknown only 'any' rules match, so an assay configured solely
 * as male/female variants resolves to null rather than silently applying one
 * sex's thresholds to everybody (the pre-migration HDL failure).
 */
export function resolveDisplayRule(rules: BiomarkerDisplayRule[], target: ResolveTarget): BiomarkerDisplayRule | null {
    const active = rules.filter((r) => r.is_active);
    if (active.length === 0) return null;

    const loinc = target.loinc?.trim() || null;
    const assayCode = target.assayCode?.trim() || null;
    const name = target.testName ? normalizeName(target.testName) : null;

    const sexes: RuleSex[] = target.sex ? [target.sex, 'any'] : ['any'];

    const matchers: Array<(r: BiomarkerDisplayRule) => boolean> = [];
    if (loinc) matchers.push((r) => r.loinc === loinc);
    if (assayCode) matchers.push((r) => r.assay_code === assayCode && noContradiction(loinc, r.loinc));
    if (name) {
        matchers.push(
            (r) =>
                r.name_aliases.some((a) => a.toLowerCase() === name) &&
                noContradiction(loinc, r.loinc) &&
                noContradiction(assayCode, r.assay_code)
        );
    }

    for (const matches of matchers) {
        for (const sex of sexes) {
            const found = active.find((r) => r.sex === sex && matches(r));
            if (found) return found;
        }
    }

    return null;
}
